import React, { useState, useCallback } from 'react';
import { 
  Card, 
  Space,
  Tooltip,
  Popover
} from 'antd';
import { 
  ExperimentOutlined, 
  PlusCircleOutlined,
  InfoCircleOutlined,
  CheckCircleFilled,
  DeleteOutlined
} from '@ant-design/icons';
import './testCard.scss';
import { TextHelper } from '../../helpers/text.helper';
import ReqCode from '../../enums/reqCode.enum';
import CollectionMethod from '../../enums/collectionMethod.enum';
import { TestChartDrawer } from '../testChartDrawer/testChartDrawer.component';
const { Meta } = Card;

const TestCard = ({item, isPanel, addToPanel, removeFromPanel, isInCart, isInCartPanel}) => {
  const [isJustAdded, setIsJustAdded] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [popoverVisible, setPopoverVisible] = useState(false);

  const handleViewDetails = useCallback(() => {
    setDrawerOpen(true);
  }, []);

  const handleAddToPanel = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  
    setTimeout(() => {
      addToPanel(item);
      setIsJustAdded(true);
      setTimeout(() => {
        setIsJustAdded(false);
      }, 1500);

    }, 0);
  }, [addToPanel, item]);

  const handleRemoveFromPanel = useCallback((e) => {
    e.stopPropagation();
    removeFromPanel(item);
  }, [removeFromPanel, item]);

  const requirementsContent = (
    <div className="requirements-popover">
      <h4>Test Restrictions</h4>
      <div className="requirements-list">
      {(item.reqCodes.includes(ReqCode.LAB_ONLY) ||
          item.reqCodes.includes(ReqCode.FROZEN)) && (
          <div className="requirement-item">
            <span className="requirement-icon">🏥</span>
            <div className="requirement-text">
              <div className="requirement-title">Collection Method</div>
              <div className="requirement-description">{item.collectionMethods.includes(CollectionMethod.BLOOD) ? 'Due to processing requirements, blood collection must be done at a lab (no at-home blood draw available).' : item.collectionMethods.includes(CollectionMethod.URINE) ? 'Due to processing requirements, urine collection must be done at a lab' : 'Due to processing requirements, specimen collection must be done at a lab'}</div>
            </div>
          </div>
        )}
        {item.reqCodes.includes(ReqCode.NO_NY) && (
          <div className="requirement-item">
            <span className="requirement-icon">📍</span>
            <div className="requirement-text">
              <div className="requirement-title">Location Restriction</div>
              <div className="requirement-description">Currently unavailable in AZ, HI, NJ, NY.<p>If you live in these states and would like to order this test, please email concierge@instalab.com to discuss alternative options.</p></div>
            </div>
          </div>
        )}


      </div>
    </div>
  );

  const actions = () => [
    <div key="view" className="test-card-action" onClick={handleViewDetails}>
      <Space>
        <InfoCircleOutlined />
        <span>Learn More</span>
      </Space>
    </div>,
    isInCart ? (
      <div 
        key="added" 
        className={`test-card-action-added ${isJustAdded ? 'just-added' : ''}`}
        onClick={isJustAdded ? undefined : handleRemoveFromPanel}
      >
        <div className="default-state">
          <CheckCircleFilled />
          <span>Added</span>
        </div>
        {!isJustAdded && (
          <div className="hover-state">
            <DeleteOutlined />
            <span>Remove</span>
          </div>
        )}
      </div>
    ) :
    isInCartPanel ? (
      <Tooltip title={`This test is part of ${isInCartPanel?.name ? `the ${isInCartPanel?.name}, which you've already added` : 'a selected panel'}. To remove this test, remove that panel.`}>
        <div 
          key="included" 
          className="test-card-action-added included"
          
      >
        <CheckCircleFilled />
          <span>Included</span>
        </div>
      </Tooltip>
    ) :
    
    (
      <div key="add" className="test-card-action" onClick={handleAddToPanel}>
        <Space>
          <PlusCircleOutlined />
          <span>Add to Panel</span>
        </Space>
      </div>
    )
  ];
  
  return (
    <Card 
      className={`test-card flat ${isPanel ? 'panel-card' : ''}`}
      hoverable
      actions={actions()}
      key={item._id}
    >
      <Meta
        title={
          <div className="test-title">
            <div className="test-long-name">{item.shortName || item.name}</div>

            {isPanel && item.tests && item.tests.length > 1 ? 
              <span className="biomarker-count">
                <ExperimentOutlined /> {item.tests.length} biomarkers
              </span>
            :
              item.longName !== (item.shortName || item.name) && <div className="test-short-name">{item.longName}</div>
            }
          </div>
        }
        description={
          <>
            {item.tagline && (
              <div className="item-tagline">
                {item.tagline}
                
              </div>
            )}

            
            <div className="price-section">
              {item.cost > 0 ? (
                <span className="price-tag">
                  <span className="currency">$</span>
                  <span className="amount">{item.cost}</span>
                </span>
              ) : item.productType && item.productType.lowestPrice && item.productType.highestPrice ? (
                <span className="price-tag price-tag-location-based">
                  {TextHelper.formatCurrency(item.productType.lowestPrice)} - {TextHelper.formatCurrency(item.productType.highestPrice)}
                </span>
              ) : null}
              {item.reqCodes && (item.reqCodes.includes(ReqCode.NO_NY) || item.reqCodes.includes(ReqCode.LAB_ONLY)) && (
                <Popover
                  content={requirementsContent}
                  title={null}
                  trigger={["click", "hover"]}
                  open={popoverVisible}
                  onOpenChange={setPopoverVisible}
                  placement="right"
                  overlayInnerStyle={{
                    maxWidth: '400px'
                  }}
                >
                  <span className="requirements-trigger">
                    <InfoCircleOutlined /> See Restrictions
                  </span>
                </Popover>
              )}
            </div>
          </>
        }
      />

      {drawerOpen && (
        <TestChartDrawer
          open={drawerOpen}
          setOpen={setDrawerOpen}
          test={item}
          addToCart={addToPanel}
          setTest={null}
        />
      )}

    </Card>
  );
}

export default TestCard;
